import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate, useRouteContext } from '@tanstack/react-router';
import { Button, Flex, Modal, Radio, Result, Space, message } from 'antd';
import { useState } from 'react';
import { z } from 'zod';

import {
  getGetBillingOverviewQueryKey,
  useCreateBillingCheckoutSession,
  useCreateBillingPortalSession,
  useGenerateBillingTestUsage,
  useGetBillingOverview,
  useGetBillingPlanTiers,
} from '../../../../api/generated/api';
import { usePermissions } from '../../../../modules/account/hooks';
import { getAttribution } from '../../../../modules/attribution';
import {
  BillingOverview,
  ManagePlanDrawer,
  PlanSelect,
  TopUpModal,
  TRIAL_DAYS,
  useRefreshBillingContext,
} from '../../../../modules/billing';
import { SettingsLayoutContent } from '../../../../ui/components/layout';
import { Loader } from '../../../../ui/components/loader';

import type { GenerateBillingTestUsageBodyDtoScenarioType } from '../../../../api/generated/model/generateBillingTestUsageBodyDtoScenarioType';

const BillingSearchSchema = z.object({
  upgrade: z.coerce.number().optional(),
  buy: z.coerce.number().optional(),
});

const SettingsBillingRoute = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { selectedOrganization } = useRouteContext({ from: '/_authenticated/_app' });
  const { can } = usePermissions();
  const canManageBilling = can('settings', 'manageBilling');
  const organizationId = selectedOrganization.id;
  const search = Route.useSearch();

  const [testUsageModalOpen, setTestUsageModalOpen] = useState(false);
  const [testUsageScenario, setTestUsageScenario] = useState<GenerateBillingTestUsageBodyDtoScenarioType>('healthy');

  const planOpen = canManageBilling && search.upgrade === 1;
  const openPlan = () =>
    void navigate({ to: '/settings/billing', search: { upgrade: 1 }, replace: false });
  const closePlan = () => void navigate({ to: '/settings/billing', search: {}, replace: true });

  const buyOpen = canManageBilling && search.buy === 1;
  const closeBuy = () => void navigate({ to: '/settings/billing', search: {}, replace: true });
  const refreshBillingContext = useRefreshBillingContext();

  const { data: overviewResponse, status, refetch } = useGetBillingOverview(organizationId);
  const checkoutMutation = useCreateBillingCheckoutSession();
  const portalMutation = useCreateBillingPortalSession();
  const generateTestUsageMutation = useGenerateBillingTestUsage({
    mutation: {
      onSuccess: (_data, vars) => {
        void refreshBillingContext(vars.organizationId);
      },
    },
  });

  const overview = overviewResponse?.data;

  // The plan picker of the "Set up your subscription" state; an existing plan picks in the drawer.
  const [subscribePriceId, setSubscribePriceId] = useState<string>();
  const { data: planTiersResponse } = useGetBillingPlanTiers(organizationId, {
    query: {
      enabled: canManageBilling && status === 'success' && !overview?.subscription?.stripeSubscriptionId,
      staleTime: 5 * 60_000,
    },
  });
  const planTiers = planTiersResponse?.data.tiers ?? [];

  if (!canManageBilling) {
    return (
      <SettingsLayoutContent title="Billing" description="Billing and subscription management.">
        <Result status="403" title="No access to billing settings" />
      </SettingsLayoutContent>
    );
  }

  if (status === 'pending') {
    return (
      <SettingsLayoutContent title="Billing" description="Billing and subscription management.">
        <Loader />
      </SettingsLayoutContent>
    );
  }

  if (status === 'error') {
    return (
      <SettingsLayoutContent title="Billing" description="Billing and subscription management.">
        <Result
          status="error"
          title="Failed to load billing"
          extra={
            <Button type="primary" onClick={() => void refetch()}>
              Retry
            </Button>
          }
        />
      </SettingsLayoutContent>
    );
  }

  const hasSubscription = Boolean(overview?.subscription?.stripeSubscriptionId);

  const startCheckout = async (priceId?: string) => {
    try {
      const result = await checkoutMutation.mutateAsync({
        organizationId,
        data: { attribution: getAttribution(), priceId },
      });
      void queryClient.invalidateQueries({
        queryKey: getGetBillingOverviewQueryKey(organizationId),
      });
      const url = result.data.url;
      if (url) {
        window.location.href = url;
      }
    } catch {
      message.error('Failed to start checkout. Please try again.');
    }
  };

  if (!hasSubscription) {
    return (
      <SettingsLayoutContent title="Billing" description="Billing and subscription management.">
        <Result
          status="info"
          title="Set up your subscription"
          subTitle={`Start a ${TRIAL_DAYS}-day trial with a card on file. You can manage invoices and payment methods in Stripe.`}
          extra={
            <Flex vertical align="center" gap={12}>
              {planTiers.length > 1 && (
                <div style={{ width: 280 }}>
                  <PlanSelect
                    tiers={planTiers}
                    value={subscribePriceId ?? planTiersResponse?.data.defaultPriceId}
                    onChange={setSubscribePriceId}
                    disabled={checkoutMutation.isPending}
                    testId="billing-subscribe-plan"
                  />
                </div>
              )}
              <Button
                type="primary"
                data-testid="billing-subscribe"
                loading={checkoutMutation.isPending}
                onClick={() => void startCheckout(subscribePriceId)}
              >
                Subscribe
              </Button>
            </Flex>
          }
        />
      </SettingsLayoutContent>
    );
  }

  const handlePortal = async () => {
    try {
      const result = await portalMutation.mutateAsync({ organizationId });
      void queryClient.invalidateQueries({
        queryKey: getGetBillingOverviewQueryKey(organizationId),
      });
      const url = result.data.url;
      if (url) {
        window.location.href = url;
      }
    } catch {
      message.error('Failed to open the billing portal. Please try again.');
    }
  };

  return (
    <>
      <SettingsLayoutContent
        title="Billing"
        description="Billing and subscription management."
        action={
          import.meta.env.VITE_ENV !== 'production' && (
            <Button danger onClick={() => setTestUsageModalOpen(true)} data-testid="billing-test-usage">
              Generate test usage
            </Button>
          )
        }
      >
        {overview && (
          <BillingOverview
            organizationId={organizationId}
            overview={overview}
            portalLoading={portalMutation.isPending}
            resubscribeLoading={checkoutMutation.isPending}
            onManagePlan={openPlan}
            onOpenPortal={() => void handlePortal()}
            onResubscribe={openPlan}
          />
        )}
      </SettingsLayoutContent>

      <TopUpModal
        open={buyOpen && Boolean(overview)}
        organizationId={organizationId}
        topUp={overview?.topUp ?? null}
        preferredPriceId={overview?.autoRecharge?.pack.priceId}
        onClose={closeBuy}
      />

      {overview && (
        <ManagePlanDrawer
          open={planOpen}
          organizationId={organizationId}
          overview={overview}
          portalLoading={portalMutation.isPending}
          resubscribeLoading={checkoutMutation.isPending}
          onOpenPortal={() => void handlePortal()}
          onResubscribe={(priceId) => void startCheckout(priceId)}
          onClose={closePlan}
        />
      )}

      <Modal
        title="Generate test billing usage"
        open={testUsageModalOpen}
        onCancel={() => setTestUsageModalOpen(false)}
        okText="Generate"
        okButtonProps={{ 'data-testid': 'billing-test-usage-submit' }}
        confirmLoading={generateTestUsageMutation.isPending}
        onOk={async () => {
          try {
            const res = await generateTestUsageMutation.mutateAsync({
              organizationId,
              data: { scenarioType: testUsageScenario },
            });
            message.success(`Balance is now ${Math.floor(res.data.balance).toLocaleString()} credits`);
            setTestUsageModalOpen(false);
          } catch {
            message.error('Failed to generate test usage (API may be production-only)');
          }
        }}
      >
        <Radio.Group
          value={testUsageScenario}
          onChange={(e) => {
            setTestUsageScenario(e.target.value as GenerateBillingTestUsageBodyDtoScenarioType);
          }}
        >
          <Space orientation="vertical">
            <Radio value="healthy" data-testid="billing-test-usage-healthy">
              Healthy (60% of the monthly credits left)
            </Radio>
            <Radio value="low" data-testid="billing-test-usage-low">
              Low (8% left — under 10,000)
            </Radio>
            <Radio value="exhausted" data-testid="billing-test-usage-exhausted">
              Exhausted (0 left)
            </Radio>
            <Radio value="debt" data-testid="billing-test-usage-debt">
              In debt (0 left, 1,240 owed)
            </Radio>
            <Radio value="renewal_gap" data-testid="billing-test-usage-renewal_gap">
              Renewal gap (the plan period ended, the renewal is not collected yet)
            </Radio>
            <Radio value="lost_top_up" data-testid="billing-test-usage-lost_top_up">
              Lost top-up webhook (the newest top-up was never granted)
            </Radio>
          </Space>
        </Radio.Group>
      </Modal>
    </>
  );
};

export const Route = createFileRoute('/_authenticated/_app/settings/billing')({
  component: SettingsBillingRoute,
  validateSearch: (params) => BillingSearchSchema.parse(params),
});
